import { mapFormStateToUpdateInput } from '@griply/services';
import {
  ActionEvents,
  CalendarEventFormFields,
  CalendarEventGuestNotification,
  CalendarEventOccurrenceTarget,
  CalendarEventRef,
  CalendarEventResponseStatus,
  CalendarEventVideoCallProvider,
  CalendarEventWarning,
  ExternalConnectionsProviders,
} from '@griply/types';
import {
  buildCreateEventFormFields,
  buildEventFormOptimisticEvent,
  canConnectionWriteEvents,
  CreateCalendarEventInitial,
  getCalendarEventErrorMessage,
  getCalendarEventProposeTimeUrl,
  getConnectionCalendar,
  getConnectionProvider,
  getContactsCapableConnectionIds,
  getDefaultEventCalendar,
  getTimeFormat,
  getWritableConnectionCalendars,
} from '@griply/utils';
import {
  useCreateCalendarEventAllowed,
  useEditCalendarEventAllowed,
  useExternalCalendarConnections,
  useLocationSearch,
} from 'features/external-connections';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EventApplyToOccurrencesDialog,
  EventFormPopup,
  EventMoveBlockedDialog,
} from 'shared/components/ui/event-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useOpenSettings } from 'shared/contexts/user-settings';
import { SettingsSection } from 'shared/dialogs/user-settings/settings-nav';
import { useCreateExternalCalendarEventMutation } from 'shared/hooks/queries/use-create-external-calendar-event-mutation';
import { useDeleteExternalCalendarEventMutation } from 'shared/hooks/queries/use-delete-external-calendar-event-mutation';
import { useExternalCalendarEventQuery } from 'shared/hooks/queries/use-external-calendar-event-query';
import { useExternalConnectionsCalendarsQueries } from 'shared/hooks/queries/use-external-connections-calendars-queries';
import { useExternalConnectionsContactsQueries } from 'shared/hooks/queries/use-external-connections-contacts-queries';
import { useRespondToExternalCalendarEventMutation } from 'shared/hooks/queries/use-respond-to-external-calendar-event-mutation';
import { useUpdateExternalCalendarEventMutation } from 'shared/hooks/queries/use-update-external-calendar-event-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useUser } from 'shared/hooks/use-user';
import { getErrorReporter } from 'shared/services/error-tracking';
import { initialDateFormat } from 'shared/types/date-format-options';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import {
  EventFormContext,
  EventFormContextProps,
  EventPopupAnchor,
  OpenEditEventOptions,
} from './event-form-context';
import { useEventDragFlow } from './use-event-drag-flow';

export type EventFormProviderProps = {
  children: React.ReactNode;
};

type EditState = {
  target: CalendarEventRef;
  anchor: EventPopupAnchor;
  seed: CalendarEventFormFields;
  url?: string;
};

type CreateState = {
  initial: CreateCalendarEventInitial;
  anchor: EventPopupAnchor;
};

export const EventFormProvider: React.FC<EventFormProviderProps> = ({
  children,
}) => {
  const [create, setCreate] = useState<CreateState | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const lastSubmitRef = useRef<{
    fields: CalendarEventFormFields;
    occurrenceTarget: CalendarEventOccurrenceTarget;
    // replayed on retry, so a retry cannot undo a "don't send" the user already gave
    guestNotification: CalendarEventGuestNotification | null;
  } | null>(null);

  const { t } = useTranslation();
  const user = useUser();
  const track = useTrackEvents();
  const openSettings = useOpenSettings();
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: createEventAllowed } = useCreateCalendarEventAllowed();
  const { allowed: editAllowed } = useEditCalendarEventAllowed();

  const popupOpen = !!create || !!edit;

  const connections = useExternalCalendarConnections();
  const connectionIds = useMemo(
    () => connections.map(({ id }) => id),
    [connections],
  );
  // fetched only while the popup is open — a root-mounted provider must not
  // fan out callables on every app boot
  const { calendars, isPending: calendarsPending } =
    useExternalConnectionsCalendarsQueries(connectionIds, {
      enabled: popupOpen,
    });

  // one pool across all capable connections
  const contactsConnectionIds = useMemo(
    () => getContactsCapableConnectionIds(connections),
    [connections],
  );
  const contacts = useExternalConnectionsContactsQueries(
    contactsConnectionIds,
    { enabled: popupOpen },
  );

  const selectableCalendars = useMemo(
    () =>
      getWritableConnectionCalendars(connections, calendars, {
        requireCalendarWrite: true,
      }),
    [calendars, connections],
  );

  const {
    details: editDetails,
    isLoadingDetails,
    isError: isEditLoadError,
    refetch: refetchEditDetails,
  } = useExternalCalendarEventQuery(edit?.target ?? null);

  // the baseline the provider stamped the event's zone against — anything else is a user override
  const calendarTimeZone = useMemo(
    () =>
      editDetails
        ? getConnectionCalendar(calendars, {
            connectionId: editDetails.connectionId,
            calendarId: editDetails.calendarId,
          })?.timeZone
        : undefined,
    [calendars, editDetails],
  );

  const createEvent = useCreateExternalCalendarEventMutation();
  const deleteEvent = useDeleteExternalCalendarEventMutation();
  const update = useUpdateExternalCalendarEventMutation();
  const respond = useRespondToExternalCalendarEventMutation();
  const locationSearch = useLocationSearch();

  const {
    pendingDrag,
    blockedMove,
    onDragEvent,
    onSelectDragOccurrences,
    onCancelDragOccurrences,
    closeBlockedMove,
  } = useEventDragFlow({ connections, editAllowed, update });

  const closePopup = useCallback(() => {
    setCreate(null);
    setEdit(null);
    lastSubmitRef.current = null;
    createEvent.reset();
    update.reset();
    respond.reset();
  }, [createEvent, respond, update]);

  // TanStack owns the write result. The popup is dismissed the moment a save starts, so a
  // non-empty list means "bring it back and show this"
  // A refusal the provider explained (an unstorable repeat rule, a Teams meeting that cannot be
  // removed) says which field to change; a load failure has nothing to add over the generic copy.
  const errorDescription = useMemo(() => {
    const error = createEvent.error ?? update.error;
    return error ? getCalendarEventErrorMessage(error, t) : undefined;
  }, [createEvent.error, update.error, t]);

  const warnings = useMemo<CalendarEventWarning[] | null>(() => {
    const result = createEvent.data ?? update.data;
    return result?.warnings.length ? result.warnings : null;
  }, [createEvent.data, update.data]);

  // Connection-level, not calendar-level: the per-calendar canWrite flags sit behind the
  // calendars callables, which only fetch while the popup is open — so at open time an empty
  // selectableCalendars cannot be told apart from "not fetched yet". Sending the user to the
  // Calendar settings beats opening a form whose only possible target list is empty.
  const onCreateEvent = useCallback(
    (initial: CreateCalendarEventInitial, anchor: EventPopupAnchor) => {
      if (!createEventAllowed) {
        openPremiumDialog();
        return;
      }
      if (!connections.some(canConnectionWriteEvents)) {
        openSettings(SettingsSection.Calendar);
        return;
      }
      setEdit(null);
      setCreate({ initial, anchor });
    },
    [connections, createEventAllowed, openPremiumDialog, openSettings],
  );

  const onEditEvent = useCallback(
    (target: CalendarEventRef, { anchor, seed, url }: OpenEditEventOptions) => {
      setCreate(null);
      setEdit({ target, anchor, seed, url });
    },
    [],
  );

  // shared by "save a new event" and "duplicate the open one" — both write the fields
  // exactly as they stand and close once the provider accepted them without warnings
  const createFromFields = useCallback(
    async (fields: CalendarEventFormFields) => {
      const result = await createEvent.mutateAsync(fields);
      track(ActionEvents.CalendarEventCreate, {
        provider: fields.calendar
          ? getConnectionProvider(connections, fields.calendar.connectionId)
          : ExternalConnectionsProviders.Google,
        all_day: fields.allDay,
        has_guests: !!fields.guests.length,
        has_video_call: fields.videoCall,
        is_recurring: !!fields.rrule,
      });
      return result;
    },
    [connections, createEvent, track],
  );

  const submitForm = useCallback(
    async (
      fields: CalendarEventFormFields,
      occurrenceTarget: CalendarEventOccurrenceTarget,
      guestNotification: CalendarEventGuestNotification | null,
    ) => {
      lastSubmitRef.current = { fields, occurrenceTarget, guestNotification };

      // Saving is not something to wait on: hide the popup now and only bring it back if
      // the provider actually reports a problem. The optimistic patch keeps the grid in
      // step so the change is visible the instant the popup goes.
      const restore = { create, edit };
      setCreate(null);
      setEdit(null);
      // A move recreates the event on every route but one, so reopening must use the ref the write returned.
      const reopen = (movedTo?: CalendarEventRef) => {
        setCreate(restore.create);
        setEdit(
          restore.edit && movedTo
            ? { ...restore.edit, target: movedTo }
            : restore.edit,
        );
      };

      try {
        if (edit && editDetails) {
          const result = await update.mutateAsync({
            payload: mapFormStateToUpdateInput(
              fields,
              editDetails,
              occurrenceTarget,
              guestNotification,
            ),
            optimistic: buildEventFormOptimisticEvent(fields),
          });
          track(ActionEvents.CalendarEventUpdate, {
            provider: getConnectionProvider(
              connections,
              editDetails.connectionId,
            ),
            all_day: fields.allDay,
            has_guests: !!fields.guests.length,
            has_video_call: fields.videoCall,
            is_recurring: !!fields.rrule || editDetails.isRecurring,
            edit_scope: occurrenceTarget,
            notified_guests: guestNotification
              ? guestNotification === CalendarEventGuestNotification.All
              : null,
          });
          result.warnings.length
            ? reopen({
                connectionId: result.connectionId,
                calendarId: result.calendarId,
                eventId: result.id,
              })
            : closePopup();
          return;
        }

        const result = await createFromFields(fields);
        result.warnings.length ? reopen() : closePopup();
      } catch (error) {
        // The original ref, deliberately: the backend orders its writes so a failure never relocates the event.
        reopen();
        throw error;
      }
    },
    [
      closePopup,
      connections,
      create,
      createFromFields,
      edit,
      editDetails,
      track,
      update,
    ],
  );

  const onSubmit = useCallback(
    (
      fields: CalendarEventFormFields,
      occurrenceTarget: CalendarEventOccurrenceTarget,
      guestNotification: CalendarEventGuestNotification | null,
    ) => {
      submitForm(fields, occurrenceTarget, guestNotification).catch(() => {
        // mutation error state renders the retry screen
      });
    },
    [submitForm],
  );

  const onRespond = useCallback(
    (responseStatus: CalendarEventResponseStatus) => {
      if (!edit) {
        return;
      }
      respond.mutate({
        ...edit.target,
        responseStatus,
        editScope: CalendarEventOccurrenceTarget.Single,
      });
    },
    [edit, respond],
  );

  // The popup belongs to the original event, so it must not become a status surface for the
  // copy's write — it closes first and the detached create reports its own failures.
  const onDuplicate = useCallback(
    (fields: CalendarEventFormFields) => {
      closePopup();
      createFromFields(fields).catch((error: unknown) => {
        getErrorReporter().captureException(error, {
          tags: { source: 'calendar-event-duplicate' },
        });
      });
    },
    [closePopup, createFromFields],
  );

  // Deletes the single occurrence, never the series — an events-list action must not be
  // able to wipe a whole recurrence without asking which occurrences it applies to.
  const onDelete = useCallback(() => {
    if (!edit) {
      return;
    }
    deleteEvent.mutate({
      ...edit.target,
      editScope: CalendarEventOccurrenceTarget.Single,
    });
    track(ActionEvents.CalendarEventDelete, {
      provider: getConnectionProvider(connections, edit.target.connectionId),
      is_recurring: !!editDetails?.isRecurring,
      edit_scope: CalendarEventOccurrenceTarget.Single,
    });
    closePopup();
  }, [closePopup, connections, deleteEvent, edit, editDetails, track]);

  const onRetry = useCallback(() => {
    if (isEditLoadError) {
      refetchEditDetails();
      return;
    }
    createEvent.reset();
    update.reset();
    const last = lastSubmitRef.current;
    if (!last) {
      return;
    }
    onSubmit(last.fields, last.occurrenceTarget, last.guestNotification);
  }, [createEvent, update, onSubmit, isEditLoadError, refetchEditDetails]);

  const isVideoCallLocked = useMemo(
    () =>
      !!editDetails?.videoCall &&
      getConnectionProvider(connections, editDetails.connectionId) ===
        ExternalConnectionsProviders.Outlook,
    [connections, editDetails],
  );

  // Offering the swap needs all three: a conference to swap, a provider that can remove one (Graph
  // cannot — a Teams meeting is permanent), and a conference that is not already Meet. The provider
  // must also be KNOWN: an older backend sends no videoCallProvider at all, and treating that silence
  // as "not Meet" would put a destructive action in front of a link we cannot identify.
  const canReplaceVideoCall = useMemo(
    () =>
      !!editDetails?.videoCallUrl &&
      !!editDetails?.videoCallProvider &&
      editDetails.videoCallProvider !==
        CalendarEventVideoCallProvider.GoogleMeet &&
      getConnectionProvider(connections, editDetails.connectionId) ===
        ExternalConnectionsProviders.Google,
    [connections, editDetails],
  );

  const proposeTimeUrl = useMemo(
    () =>
      edit
        ? getCalendarEventProposeTimeUrl(
            edit.url,
            getConnectionProvider(connections, edit.target.connectionId),
          )
        : undefined,
    [connections, edit],
  );

  // The create seed re-derives as the calendars resolve, so the default target calendar
  // fills in underneath any edit the user already made instead of gating the whole popup.
  const seed = useMemo(() => {
    if (edit) {
      return edit.seed;
    }
    if (!create) {
      return null;
    }
    return buildCreateEventFormFields(
      create.initial,
      getDefaultEventCalendar(connections, selectableCalendars),
    );
  }, [connections, create, edit, selectableCalendars]);

  const value = useMemo<EventFormContextProps>(
    () => ({ onCreateEvent, onEditEvent, onDragEvent }),
    [onCreateEvent, onDragEvent, onEditEvent],
  );

  return (
    <EventFormContext.Provider value={value}>
      {children}

      {popupOpen && !!seed && (
        <EventFormPopup
          anchor={(edit ?? create)!.anchor}
          seed={seed}
          details={edit ? editDetails : undefined}
          isLoadingDetails={!!edit && isLoadingDetails}
          isLoadingCalendars={calendarsPending && !!connectionIds.length}
          calendars={calendars}
          selectableCalendars={selectableCalendars}
          connections={connections}
          calendarTimeZone={calendarTimeZone}
          contacts={contacts}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          timeFormat={getTimeFormat(user)}
          isEdit={!!edit}
          isVideoCallLocked={isVideoCallLocked}
          canReplaceVideoCall={canReplaceVideoCall}
          isSaving={createEvent.isPending || update.isPending}
          isError={createEvent.isError || update.isError || isEditLoadError}
          errorDescription={errorDescription}
          warnings={warnings}
          isResponding={respond.isPending}
          hasRespondError={respond.isError}
          proposeTimeUrl={proposeTimeUrl}
          canEditEvents={editAllowed}
          eventUrl={edit?.url}
          locationSearch={locationSearch}
          onSubmit={onSubmit}
          onRespond={onRespond}
          onUnlockEdit={openPremiumDialog}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onRetry={onRetry}
          onClose={closePopup}
        />
      )}

      {!!pendingDrag && (
        <EventApplyToOccurrencesDialog
          onSelect={onSelectDragOccurrences}
          onClose={onCancelDragOccurrences}
        />
      )}

      {!!blockedMove && (
        <EventMoveBlockedDialog
          provider={blockedMove.provider}
          url={blockedMove.url}
          onClose={closeBlockedMove}
        />
      )}
    </EventFormContext.Provider>
  );
};
